import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface OrganizerEvent {
  id: string;
  title: string;
  sport: string;
  date: string;
  time: string;
  location: string;
  participants: number;
  maxParticipants: number;
  registrationFee: number;
  status: string;
  eventType: 'tournament' | 'league';
  imageUrl?: string;
  endDate?: string;
  leagueFormat?: string;
  matchType?: 'singles' | 'dobles' | 'ambos';
  genderCategories?: string[];
  subcategories?: Record<string, string[]>;
  registrationDeadline?: string;
  total_weeks?: number;
  matches_per_week?: number;
  address?: string;
  postal_code?: string;
  organizer_id?: string;
  rules?: string;
  category?: string;
  gender_category?: string;
  subcategory?: string;
  start_time?: string;
  end_time?: string;
  game_days?: string[];
  league_format?: string;
  description?: string;
}

export interface OrganizerSponsor {
  id: string;
  name: string;
  logoUrl: string | null;
}

export interface OrganizerProfileData {
  id: string;
  organizerId: string;
  userId: string;
  name: string;
  description: string;
  website: string;
  avatar: string;
  rating: number;
  sports: string[];
  
  totalEvents: number;
  tournamentsCount: number;
  leaguesCount: number;
  followersCount: number;
  sponsors: OrganizerSponsor[];
  
  upcomingTournaments: OrganizerEvent[];
  upcomingLeagues: OrganizerEvent[];
  pastTournaments: OrganizerEvent[];
  pastLeagues: OrganizerEvent[];
}

const transformEvent = (event: any): OrganizerEvent => ({
  id: event.id,
  title: event.title,
  sport: event.sport,
  date: event.start_date,
  time: event.start_time || '09:00',
  location: event.location,
  participants: 0,
  maxParticipants: event.max_participants || 0,
  registrationFee: event.entry_fee || 0,
  status: event.status,
  eventType: event.event_type,
  imageUrl: event.image_url,
  endDate: event.end_date || undefined,
  leagueFormat: event.league_format === 'escalera' ? 'escalera-piramidal' : event.league_format || undefined,
  matchType: event.match_type || undefined,
  genderCategories: event.gender_category && event.category
    ? [`${event.gender_category.charAt(0).toUpperCase() + event.gender_category.slice(1)} ${event.category}`]
    : event.category ? [event.category] : [],
  subcategories: event.gender_category && event.category && event.subcategory
    ? { [`${event.gender_category.charAt(0).toUpperCase() + event.gender_category.slice(1)} ${event.category}`]: event.subcategory.split(',').map((s: string) => s.trim()) }
    : {},
  registrationDeadline: event.registration_deadline || undefined,
  total_weeks: event.total_weeks || undefined,
  matches_per_week: event.matches_per_week || undefined,
  address: event.address || undefined,
  postal_code: event.postal_code || undefined,
  organizer_id: event.organizer_id,
  rules: event.rules || undefined,
  category: event.category || undefined,
  gender_category: event.gender_category || undefined,
  subcategory: event.subcategory || undefined,
  start_time: event.start_time || undefined,
  end_time: event.end_time || undefined,
  game_days: event.game_days || undefined,
  league_format: event.league_format || undefined,
  description: event.description || undefined,
});

export function useOrganizerProfile(organizerId: string | null) {
  const [profile, setProfile] = useState<OrganizerProfileData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!organizerId) {
      setLoading(false);
      return;
    }

    const fetchOrganizerProfile = async () => {
      try {
        setLoading(true);
        setError(null);

        const { data: publicProfile, error: profileError } = await supabase
          .from('organizer_public_profiles')
          .select('*')
          .eq('organizer_id', organizerId)
          .maybeSingle();

        if (profileError) throw profileError;
        if (!publicProfile) {
          setError('Organizador no encontrado');
          setLoading(false);
          return;
        }

        const { data: organizerData, error: organizerError } = await supabase
          .from('organizers')
          .select('id, user_id')
          .eq('id', organizerId)
          .maybeSingle();

        if (organizerError) throw organizerError;
        const userId = organizerData?.user_id || publicProfile.user_id;
        
        if (!userId) {
          setError('No se pudo determinar el user_id del organizador');
          setLoading(false);
          return;
        }

        const today = new Date().toISOString().split('T')[0];

        const [
          totalCountResult,
          tournamentsCountResult,
          leaguesCountResult,
          upcomingTournamentsResult,
          upcomingLeaguesResult,
          pastTournamentsResult,
          pastLeaguesResult,
          followersCountResult,
          sponsorsResult,
        ] = await Promise.all([
          supabase.from('events').select('id', { count: 'exact' }).eq('organizer_id', userId).eq('is_published', true).range(0, 0),
          supabase.from('events').select('id', { count: 'exact' }).eq('organizer_id', userId).eq('is_published', true).eq('event_type', 'tournament').range(0, 0),
          supabase.from('events').select('id', { count: 'exact' }).eq('organizer_id', userId).eq('is_published', true).eq('event_type', 'league').range(0, 0),
          supabase.from('events').select('*').eq('organizer_id', userId).eq('is_published', true).eq('event_type', 'tournament').eq('status', 'upcoming').gte('start_date', today).order('start_date', { ascending: true }).limit(10),
          supabase.from('events').select('*').eq('organizer_id', userId).eq('is_published', true).eq('event_type', 'league').eq('status', 'upcoming').gte('start_date', today).order('start_date', { ascending: true }).limit(10),
          supabase.from('events').select('*').eq('organizer_id', userId).eq('is_published', true).eq('event_type', 'tournament').in('status', ['completed', 'ongoing']).order('start_date', { ascending: false }).limit(10),
          supabase.from('events').select('*').eq('organizer_id', userId).eq('is_published', true).eq('event_type', 'league').in('status', ['completed', 'ongoing']).order('start_date', { ascending: false }).limit(10),
          supabase.from('organizer_followers').select('id', { count: 'exact' }).eq('organizer_id', organizerId).range(0, 0),
          supabase.from('organizer_sponsors').select('*').eq('organizer_id', userId),
        ]);

        const sponsors: OrganizerSponsor[] = (sponsorsResult.data || []).map((s: any) => ({
          id: s.id,
          name: s.name,
          logoUrl: s.logo_url,
        }));

        const profileData: OrganizerProfileData = {
          id: publicProfile.id,
          organizerId: publicProfile.organizer_id || organizerId,
          userId,
          name: publicProfile.company_name || 'Organizador',
          description: publicProfile.description || '',
          website: publicProfile.website || '',
          avatar: publicProfile.avatar_url || '',
          rating: publicProfile.rating || 0,
          sports: publicProfile.sports || [],
          
          totalEvents: totalCountResult.count || 0,
          tournamentsCount: tournamentsCountResult.count || 0,
          leaguesCount: leaguesCountResult.count || 0,
          followersCount: followersCountResult.count || 0,
          sponsors,
          
          upcomingTournaments: (upcomingTournamentsResult.data || []).map(transformEvent),
          upcomingLeagues: (upcomingLeaguesResult.data || []).map(transformEvent),
          pastTournaments: (pastTournamentsResult.data || []).map(transformEvent),
          pastLeagues: (pastLeaguesResult.data || []).map(transformEvent),
        };

        setProfile(profileData);
      } catch (err: any) {
        console.error('Error fetching organizer profile:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchOrganizerProfile();
  }, [organizerId]);

  return { profile, loading, error };
}
